import React, { useCallback, useMemo } from 'react';
import { useCommonStyles } from '../commonStyles';
import { createUseStyles } from 'react-jss';
import {
  BorderColors,
  Colors,
  BorderRadius,
  Spacing,
  FontSizes,
  FontWeights,
  Icon,
  IconType,
  SelectOption,
} from '@ateams/components';
import { RoleExpertiseScoreItem } from './RoleExpertiseScoreItem';
import TooltipWrapped from '@src/components/TooltipWrapped';
import { FieldCheckbox } from '../general/FieldCheckbox';
import {
  VettingProcessFeedbackRoleExpertiseScore,
  VettingProcessFeedbackRoleExpertiseScoreType,
} from '@ateams/api/dist/endpoints/vetting-process-feedback-v2';
import { useFormContext } from 'react-hook-form';
import { useStores } from '@src/stores';
import { useQueryTalentSpecializationRoles } from '@src/rq/specializations';
import { FieldDropdown } from '../general/FieldDropdown';
import { observer } from 'mobx-react';

const useStyles = createUseStyles({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.backgroundLight,
    borderRadius: BorderRadius.xLarge,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: Spacing.medium,
    border: `1px solid ${BorderColors.light}`,
    borderRadius: BorderRadius.xLarge,
    backgroundColor: Colors.backgroundWhite,
    padding: `${Spacing.xLarge}px ${Spacing.large}px`,
  },
  headersContainer: {
    display: 'grid',
    gridTemplateColumns: '220px 148px 1fr',
    gap: Spacing.medium,
  },
  headerCell: {
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semiBold,
  },
  rolesContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    borderTop: `1px solid ${BorderColors.light}`,
    margin: 0,
    marginBottom: Spacing.large,
  },
  appliedHeader: {
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semiBold,
  },
  additionalHeader: {
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semiBold,
  },
  appliedHeaderContainer: {
    display: 'flex',
    gap: Spacing.xsmall,
    marginBottom: Spacing.small,
  },
  additionalHeaderContainer: {
    display: 'flex',
    gap: Spacing.xsmall,
    marginBottom: Spacing.small,
  },
  infoIcon: {
    cursor: 'pointer',
  },
  tooltipText: {
    fontSize: FontSizes.small,
    maxWidth: '230px',
    padding: 0,
    margin: 0,
  },
  otherContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: Spacing.large,
  },
  otherHeader: {
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semiBold,
    paddingBottom: Spacing.small,
  },
  otherFieldsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: Spacing.small,
  },
  checkbox: {
    padding: Spacing.small,
    borderRadius: BorderRadius.small,
    border: `1px solid ${BorderColors.light}`,
    backgroundColor: Colors.backgroundWhite,
  },
  addRoleContainer: {
    marginTop: 8,
    width: 176,
  },
});

interface RoleExpertiseScoresSectionProps {
  talentCategoryId?: string;
  rolesAddedByTheVetter: string[];
  setRolesAddedByTheVetter: (roles: string[]) => void;
}

const RoleExpertiseScoresSection = ({
  talentCategoryId,
  rolesAddedByTheVetter,
  setRolesAddedByTheVetter,
}: RoleExpertiseScoresSectionProps) => {
  const commonStyles = useCommonStyles();
  const styles = useStyles();
  const store = useStores();
  const { auth } = store;

  const { watch, setValue } = useFormContext();

  const roleExpertiseScores: VettingProcessFeedbackRoleExpertiseScore[] =
    watch('roleExpertiseScores') || [];
  const individualContributor: boolean = watch('individualContributor');
  const teamMember: boolean = watch('teamMember');
  const teamLead: boolean = watch('teamLead');
  const consultantStrategist: boolean = watch('consultantStrategist');
  const cSuiteLevel: boolean = watch('cSuiteLevel');

  const primaryRoleExpertiseScore = roleExpertiseScores.find(
    (roleExpertiseScore) =>
      roleExpertiseScore.roleType ===
      VettingProcessFeedbackRoleExpertiseScoreType.PRIMARY,
  );

  const { data: roles } = useQueryTalentSpecializationRoles({
    query: '',
    talentCategoryId,
    enabled: !!talentCategoryId,
  });

  const appliedRoleExpertiseScores: VettingProcessFeedbackRoleExpertiseScore[] =
    roleExpertiseScores.filter(
      (roleExpertiseScore) =>
        roleExpertiseScore.roleType ===
        VettingProcessFeedbackRoleExpertiseScoreType.APPLIED,
    );

  const additionalRoleExpertiseScores: VettingProcessFeedbackRoleExpertiseScore[] =
    roleExpertiseScores.filter(
      (roleExpertiseScore) =>
        roleExpertiseScore.roleType ===
        VettingProcessFeedbackRoleExpertiseScoreType.ADDITIONAL,
    );

  const availableAdditionalRolesOptions: SelectOption[] = useMemo(() => {
    const availableAdditionalRoles = roles?.filter(
      (role) => !roleExpertiseScores.some((s) => s.roleId === role.id),
    );

    return (
      availableAdditionalRoles?.map((role) => ({
        label: role.name,
        value: role.id,
      })) || []
    );
  }, [roles, roleExpertiseScores]);

  const handleAddRole = (option: SelectOption | null) => {
    if (option?.value) {
      setRolesAddedByTheVetter([...rolesAddedByTheVetter, option?.value]);
      setValue('roleExpertiseScores', [
        ...roleExpertiseScores,
        {
          roleId: option?.value,
          roleName: option?.label,
          roleType: VettingProcessFeedbackRoleExpertiseScoreType.ADDITIONAL,
          score: -1,
          reason: '',
        },
      ]);
    }
  };

  const handleDeleteRole = (roleId: string) => {
    setValue(
      'roleExpertiseScores',
      roleExpertiseScores.filter((role) => role.roleId !== roleId),
    );
    setRolesAddedByTheVetter(
      rolesAddedByTheVetter.filter((role) => role !== roleId),
    );
  };

  const handleRoleExpertiseScoreChange = useCallback(
    (
      roleId: string,
      roleExpertiseScore: VettingProcessFeedbackRoleExpertiseScore,
    ) => {
      setValue(
        'roleExpertiseScores',
        roleExpertiseScores.map((existingRoleExpertiseScore) =>
          existingRoleExpertiseScore.roleId === roleId
            ? roleExpertiseScore
            : existingRoleExpertiseScore,
        ),
      );
    },
    [roleExpertiseScores],
  );

  return (
    <div className={commonStyles.section}>
      <div className={commonStyles.sectionTitle}>Role specialisation</div>
      <div className={styles.mainContainer}>
        <div className={styles.container}>
          <div className={styles.headersContainer}>
            <div className={styles.headerCell}>Primary evaluation role *</div>
            <div className={styles.headerCell}>Level</div>
            <div className={styles.headerCell}>
              Specify the reasons for the role rating *
            </div>
          </div>
          <div className={styles.rolesContainer}>
            {primaryRoleExpertiseScore && (
              <RoleExpertiseScoreItem
                key={primaryRoleExpertiseScore.roleId}
                roleExpertiseScore={primaryRoleExpertiseScore}
                onRoleExpertiseScoreChange={(score) =>
                  handleRoleExpertiseScoreChange(
                    primaryRoleExpertiseScore.roleId,
                    score,
                  )
                }
              />
            )}
            {appliedRoleExpertiseScores.length > 0 && (
              <>
                <hr className={styles.divider} />
                <div className={styles.appliedHeaderContainer}>
                  <div className={styles.appliedHeader}>Applied roles</div>
                  <TooltipWrapped
                    html={
                      <div className={styles.tooltipText}>
                        Roles associated with the mission roles user applied to
                      </div>
                    }
                  >
                    <Icon type={IconType.Info} className={styles.infoIcon} />
                  </TooltipWrapped>
                </div>
                {appliedRoleExpertiseScores.map((appliedRoleExpertiseScore) => (
                  <RoleExpertiseScoreItem
                    key={appliedRoleExpertiseScore.roleId}
                    roleExpertiseScore={appliedRoleExpertiseScore}
                    onRoleExpertiseScoreChange={(score) =>
                      handleRoleExpertiseScoreChange(
                        appliedRoleExpertiseScore.roleId,
                        score,
                      )
                    }
                    onClearSelectedScore={() =>
                      handleRoleExpertiseScoreChange(
                        appliedRoleExpertiseScore.roleId,
                        {
                          ...appliedRoleExpertiseScore,
                          score: -1,
                        },
                      )
                    }
                  />
                ))}
              </>
            )}
            {(additionalRoleExpertiseScores.length > 0 ||
              auth.withCanAddAdditionalRolesToEvaluation) && (
              <>
                <hr className={styles.divider} />
                <div className={styles.additionalHeaderContainer}>
                  <div className={styles.additionalHeader}>
                    Additional roles
                  </div>
                  <TooltipWrapped
                    html={
                      <div className={styles.tooltipText}>
                        Other roles the user has on their profile which are
                        adjacent to the main role and within the same category
                      </div>
                    }
                  >
                    <Icon type={IconType.Info} className={styles.infoIcon} />
                  </TooltipWrapped>
                </div>
                {additionalRoleExpertiseScores.map(
                  (additionalRoleExpertiseScore) => (
                    <RoleExpertiseScoreItem
                      key={additionalRoleExpertiseScore.roleId}
                      roleExpertiseScore={additionalRoleExpertiseScore}
                      canBeDeleted={rolesAddedByTheVetter.includes(
                        additionalRoleExpertiseScore.roleId,
                      )}
                      onDelete={() =>
                        handleDeleteRole(additionalRoleExpertiseScore.roleId)
                      }
                      onRoleExpertiseScoreChange={(score) =>
                        handleRoleExpertiseScoreChange(
                          additionalRoleExpertiseScore.roleId,
                          score,
                        )
                      }
                      onClearSelectedScore={() =>
                        handleRoleExpertiseScoreChange(
                          additionalRoleExpertiseScore.roleId,
                          {
                            ...additionalRoleExpertiseScore,
                            score: -1,
                          },
                        )
                      }
                    />
                  ),
                )}
                {auth.withCanAddAdditionalRolesToEvaluation && (
                  <div className={styles.addRoleContainer}>
                    <FieldDropdown
                      key={availableAdditionalRolesOptions.length}
                      label=""
                      options={availableAdditionalRolesOptions}
                      value={undefined}
                      placeholder="Add roles..."
                      onChange={handleAddRole}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className={styles.otherContainer}>
          <div className={styles.otherHeader}>Other</div>
          <div className={styles.otherFieldsContainer}>
            <FieldCheckbox
              text={'Individual Contributor'}
              value={individualContributor}
              onChange={(value) => setValue('individualContributor', value)}
              className={styles.checkbox}
            />
            <FieldCheckbox
              text={'Team Member'}
              value={teamMember}
              onChange={(value) => setValue('teamMember', value)}
              className={styles.checkbox}
            />
            <FieldCheckbox
              text={'Team Lead'}
              value={teamLead}
              onChange={(value) => setValue('teamLead', value)}
              className={styles.checkbox}
            />
            <FieldCheckbox
              text={'Consultant / Strategist'}
              value={consultantStrategist}
              onChange={(value) => setValue('consultantStrategist', value)}
              className={styles.checkbox}
            />
            <FieldCheckbox
              text={'C-Suite Level'}
              value={cSuiteLevel}
              onChange={(value) => setValue('cSuiteLevel', value)}
              className={styles.checkbox}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const RoleExpertiseScoresSectionWithObserver = observer(
  RoleExpertiseScoresSection,
);

export { RoleExpertiseScoresSectionWithObserver as RoleExpertiseScoresSection };
